import type { ButtonProps } from '@hover/blueprint';
import React from 'react';

import type { CustomizeHandlerProps } from 'src/modules/homeownerApp/hooks';
import { useHomeownerAppContext } from 'src/modules/homeownerApp/hooks';
import { Button } from 'src/shared/components';
import type { AiDisplayImageFragment } from 'src/shared/graphql/generated';
import { TranslationNamespace, useTranslate } from 'src/shared/hooks';
import { useButtonImpression } from 'src/shared/hooks/useButtonImpression/useButtonImpression';
import { preserveNativeLinking } from 'src/shared/utils/routing';

import { useCustomizeHandler } from '../../hooks';

export interface CustomizeButtonProps
  extends Omit<ButtonProps, 'icon' | 'label'>,
    CustomizeHandlerProps {
  image: AiDisplayImageFragment;
  label?: string;
}

const CustomizeButton = ({
  category,
  image,
  label,
  state,
  ...props
}: CustomizeButtonProps) => {
  const t = useTranslate(TranslationNamespace.HOMEOWNER_APP);

  const { trackingData: contextTrackingData } = useHomeownerAppContext();
  const customizeText = label ?? t('paint.customize');

  const { onClick, tracking } = useCustomizeHandler({
    image,
    category,
    state,
  });

  const augmentedTrackingData = {
    ...contextTrackingData,
    button_text: customizeText,
    primary_cta: false,
    space_type: image.spaceType ?? undefined,
    track_id: 'customize-grid-view',
    view_type: 'grid-view',
    ...tracking,
  };

  useButtonImpression(augmentedTrackingData);

  const handleClick = async (
    event: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>,
  ) => {
    event.stopPropagation();

    await preserveNativeLinking(event, onClick);
  };

  return (
    <Button label={customizeText} onClick={handleClick} {...props}>
      {customizeText}
    </Button>
  );
};

export { CustomizeButton };
